import Skeleton from "@/app/components/ui/skeleton";
import { ChevronsUpDown, Plus, SearchIcon } from "lucide-react";
import type { BackendTableColumnT } from "@/app/types/backend-table";
import { Checkbox } from "../ui/checkbox";
import { BackendButton, backendInputClass } from "./backend-ui";
import { cn } from "@/lib/utils";

const STATUS_TAB_WIDTHS = ["w-14", "w-24", "w-20", "w-28"];

function getColumnWidthClass(column: BackendTableColumnT): string | undefined {
  return column.key === "sort_index" ? column.width : undefined;
}

type BackendListPageSkeletonPropsT = {
  title: string;
  columns: BackendTableColumnT[];
  buttonText?: string;
  /** @deprecated no longer rendered — kept for call-site compatibility */
  showFilters?: boolean;
  /** @deprecated no longer rendered — kept for call-site compatibility */
  filterPlaceholderCount?: number;
};

/**
 * Skeleton component for Backend List pages loading state.
 *
 * Keeps the real header (incl. the create button, disabled) visible to avoid
 * layout shifts, and only skeletons the table. No spinner, no search/filter
 * skeletons.
 */
export function BackendListPageSkeleton({
  title,
  columns,
  buttonText,
}: BackendListPageSkeletonPropsT) {
  return (
    <div>
      {/* Header - matches BackendListHeader */}
      <div className="flex items-center justify-between gap-3 pt-4">
        <h1 className="text-[32px] leading-tight font-semibold">{title}</h1>
        {buttonText && (
          <BackendButton type="button" variant="default" disabled>
            <Plus />
            {buttonText}
          </BackendButton>
        )}
      </div>

      {/* Filter row - matches BackendListTable (status tabs left, search right) */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-[12px] border border-[var(--c-border-card)] bg-[var(--c-card)] p-[5px]">
          {STATUS_TAB_WIDTHS.map((width) => (
            <Skeleton key={width} type="rounded" className={cn("h-7", width)} />
          ))}
        </div>

        <div className="ml-auto w-[220px] flex-none">
          <div
            className={cn(
              backendInputClass,
              "pointer-events-none relative flex h-10 items-center !bg-[var(--c-card)] pl-9"
            )}
          >
            <SearchIcon className="absolute left-3 z-10 size-4 text-[var(--c-faint)]" />
            <Skeleton type="rounded" className="h-3 w-16" />
          </div>
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="scrollbar-hide scrollbar-hide-webkit relative mt-6 w-full overflow-x-auto rounded-[16px] border border-[var(--c-border-card)] bg-white">
        <table className="w-full border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-[var(--c-surface)]">
              <th className="w-12 px-4 py-3 align-middle">
                <div className="flex items-center">
                  <Checkbox
                    className="bg-background dark:bg-background size-5 cursor-pointer"
                    disabled
                  />
                </div>
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "px-4 py-3 text-left align-middle font-[family-name:var(--font-backend-mono)] text-[10.5px] font-medium tracking-[0.07em] text-[var(--c-label)] uppercase",
                    getColumnWidthClass(column)
                  )}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    <ChevronsUpDown size={16} className="text-gray-400" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <tr
                key={i}
                className="h-16 border-t border-[var(--c-border-soft)]"
              >
                <td className="px-4 py-3 align-middle">
                  <div className="flex items-center">
                    <Skeleton type="rounded" className="h-5 w-5" />
                  </div>
                </td>
                {columns.map((column) => {
                  // Use full width for skeleton to match column width
                  // For image columns, use a fixed size to match the actual image size
                  let skeletonClassName = "w-full";

                  if (column.type === "profileImage") {
                    skeletonClassName = "h-10 w-10 rounded-full";
                  } else if (column.type === "image") {
                    skeletonClassName = "h-16 w-16";
                  } else if (column.type === "smallImage") {
                    skeletonClassName = "h-6 w-8";
                  } else if (column.type === "thumbnailImage") {
                    skeletonClassName = "h-8 w-14";
                  }

                  return (
                    <td
                      key={column.key}
                      className={cn(
                        "px-4 py-3 align-middle",
                        getColumnWidthClass(column)
                      )}
                    >
                      <div className="flex items-center">
                        <Skeleton
                          type={
                            column.type?.includes("Image") ? "rounded" : "text"
                          }
                          className={skeletonClassName}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
